/**
 * ServiceDetailScreen: the service page, shared by the Mini App and the studio website
 * (shared-client-screens D3, rows S1 to S6). Props only: each side reads its own service, open
 * days and times (through useSlotPicker), and hands in Back, the chat card, the photo viewer and
 * what Book session does. The app holds the time here and shows "Booking…" while it does; the
 * website goes straight to its checkout, which holds it (S3).
 */
import type { ComponentType } from 'react';
import type { HubServiceDetailDto, HubServiceSlot } from '@/api/types';
import { SessionDetailFrame } from './SessionDetailFrame';
import { DateTimePicker, type DateTimePickerProps } from './components/DateTimePicker';
import { DetailGalleryView } from './components/DetailGalleryView';
import { DetailHero } from './components/DetailHero';
import type { DetailLightboxProps } from './components/DetailLightboxView';
import { DetailInfoRow } from './components/DetailInfoRow';
import { DetailProviderCardView } from './components/DetailProviderCardView';
import { DetailStickyBar } from './components/DetailStickyBar';
import { ServiceStats } from './components/ServiceStats';
import { formatWhen, paymentTermsText, servicePriceText, type PaymentTermsInput } from './detailFormat';
import { SafeRichText } from '@/components/booking/SafeRichText';
import styles from './SessionDetailPage.module.css';

export type ServiceDetailContent = Pick<
  HubServiceDetailDto,
  | 'name'
  | 'summary'
  | 'description'
  | 'duration'
  | 'modality'
  | 'pricing'
  | 'imageUrl'
  | 'focalPoint'
  | 'gallery'
  | 'location'
  | 'cancellation'
  | 'acceptingBookings'
> & { payment: PaymentTermsInput };

/** The "Questions? Chat with …" card: the studio's name, the line under it and what its button opens. */
export interface DetailChat {
  businessName: string;
  line: string;
  onChat: () => void;
}

export interface ServiceDetailScreenProps {
  status: 'loading' | 'notfound' | 'ready';
  onBack: () => void;
  /** ready: the service. */
  service?: ServiceDetailContent;
  /** The photo viewer: the app's, with Telegram's back button, or the website's. */
  Lightbox: ComponentType<DetailLightboxProps>;
  /** null: no card (the studio's name is not known yet). */
  chat: DetailChat | null;
  tz: string;
  /** useSlotPicker's picker and picked time. */
  picker: DateTimePickerProps;
  selectedSlot: HubServiceSlot | null;
  /** Book session: `pending` while the app holds the time; `error` the line under the button. */
  book: { onBook: () => void; pending: boolean; error: string | null };
}

export function ServiceDetailScreen({ status, onBack, service, Lightbox, chat, tz, picker, selectedSlot, book }: ServiceDetailScreenProps) {
  if (status !== 'ready' || !service) {
    return (
      <SessionDetailFrame
        status={status === 'ready' ? 'loading' : status}
        pageTitle="Service Details"
        onBack={onBack}
        notFoundMessage="We couldn't find this service — it may have been removed."
      />
    );
  }

  const accepting = service.acceptingBookings;
  const canBook = accepting && !!selectedSlot;
  const ctaLabel = !accepting ? 'Not taking bookings' : book.pending ? 'Booking…' : canBook ? 'Book session' : 'Select a time';
  const ctaSub = canBook && !book.pending ? formatWhen(selectedSlot.startsAt, tz) : undefined;

  return (
    <SessionDetailFrame
      status="ready"
      pageTitle="Service Details"
      onBack={onBack}
      hero={<DetailHero name={service.name} summary={service.summary} imageUrl={service.imageUrl} focalPoint={service.focalPoint} />}
      bottomBar={
        <DetailStickyBar
          priceMain={servicePriceText(service.pricing)}
          terms={paymentTermsText(
            service.payment,
            service.payment.paymentRequirement !== 'NONE' && service.pricing.kind !== 'FREE' && service.pricing.amount != null,
            service.pricing.amount,
          )}
          ctaLabel={ctaLabel}
          ctaSub={ctaSub}
          onCta={book.onBook}
          disabled={!canBook || book.pending}
          error={book.error}
        />
      }
    >
      <ServiceStats duration={service.duration} modality={service.modality} pricing={service.pricing} />
      {service.description && <SafeRichText html={service.description} className={styles.desc} />}
      <DetailGalleryView gallery={service.gallery} Lightbox={Lightbox} />
      {chat && <DetailProviderCardView businessName={chat.businessName} line={chat.line} onChat={chat.onChat} />}
      <DetailInfoRow location={service.location} cancellation={service.cancellation} />
      <div className={styles.divider} />
      {!accepting ? (
        <div className={styles.closed} role="status">
          <span className={styles.closedIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          </span>
          <div>
            <div className={styles.closedTitle}>Not taking new session bookings right now</div>
            <div className={styles.closedSubtitle}>Check back soon.</div>
          </div>
        </div>
      ) : (
        <DateTimePicker {...picker} />
      )}
    </SessionDetailFrame>
  );
}
